import { axiosInstance } from '@/utils/lib/axios';
import { PendingUpload } from 'alurkerja-ui';
import classNames from 'classnames';
import _ from 'lodash';
import { Controller, FieldValues } from 'react-hook-form';
import { AlurkerjaInputType } from '../../types/InputType';
import clsx from 'clsx';
import { evaluateExpression } from '../../utils/evaluateExpression';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FaUpload, FaTrash, FaDownload } from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function InputImageUpload({ props }: {
  readonly props: AlurkerjaInputType;
}) {

  const { item: item, form: { control, getValues, watch } } = props;
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  let defaultValue = getValues(item.name);

  // Watch all form values for required_if evaluation
  const formValues = watch();

  // Calculate if field is required based on required_if expression
  const isRequiredByExpression = useMemo(() => {
    if (item.constraints?.required_if?.expression) {
      return evaluateExpression(item.constraints.required_if.expression, formValues);
    }
    return false;
  }, [item.constraints?.required_if?.expression, formValues]);

  // Combine regular required and required_if
  const isFieldRequired = Boolean(item.constraints?.required) || isRequiredByExpression;

  const onDownload = async (file: { original_url: string; name: string }) => {
    // Implement your download logic here
    const response = await axiosInstance.get(file.original_url, { responseType: 'blob' });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.request.getResponseHeader('content-disposition');
    const filename = contentDisposition ? contentDisposition.split('filename=')[1] : file.name;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (typeof defaultValue === 'string') {
    defaultValue = undefined;
  }



  // Ref to accumulate files for multiple upload
  // PendingUpload has a stale closure bug where selecting multiple files
  // at once only keeps the last file. We work around this by managing
  // the file list ourselves.
  const accumulatedFilesRef = useRef<any[]>([]);
  const [accumulatedFiles, setAccumulatedFiles] = useState<any[]>([]);
  const pendingBatchRef = useRef<any[]>([]);
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize accumulated files from defaultValue when loading existing task data
  const initializedRef = useRef(false);
  if (!initializedRef.current && defaultValue && Array.isArray(defaultValue) && item.multiple_file) {
    accumulatedFilesRef.current = defaultValue;
    setAccumulatedFiles(defaultValue);
    initializedRef.current = true;
  }

  const handleMultipleFileChange = useCallback((file: any, formOnChange: (value: any) => void) => {
    if (!file) return;

    const maxSize = (item.max_file_size) * 1024 * 1024;
    const files = Array.isArray(file) ? file : [file];

    // Only validate the newest file. PendingUpload retains rejected files in
    // its own internal state and re-emits them on subsequent picks; checking
    // the whole array would keep flagging the previously rejected file and
    // block any new valid upload.
    const newFile = files[files.length - 1];
    if (newFile && newFile.size > maxSize) {
      setFileSizeError(`File size must be less than ${item.max_file_size} MB`);
      return;
    }
    setFileSizeError(null);

    // PendingUpload calls onChange once per file due to stale closure bug.
    // Each call contains only [lastFile]. We batch these calls and combine them.
    // Collect the last element from each call (the newly added file).
    pendingBatchRef.current.push(newFile);

    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
    }

    batchTimerRef.current = setTimeout(() => {
      const newFiles = pendingBatchRef.current;
      pendingBatchRef.current = [];

      const updated = [...accumulatedFilesRef.current, ...newFiles];
      accumulatedFilesRef.current = updated;
      setAccumulatedFiles([...updated]);
      formOnChange(updated);
    }, 100);
  }, [item.max_file_size]);

  const handleRemoveFile = useCallback((index: number, formOnChange: (value: any) => void) => {
    const updated = accumulatedFilesRef.current.filter((_, i) => i !== index);
    accumulatedFilesRef.current = updated;
    setAccumulatedFiles([...updated]);
    setFileSizeError(null);
    formOnChange(updated.length > 0 ? updated : undefined);
  }, []);

  return (
    <div className='flex flex-col'>
      <label
        className={classNames("mr-1 font-medium after:text-sm", {})}
        htmlFor={item.name}
      >
        {item.label.en}
        {isFieldRequired && <span className='text-red-400 after:text-sm'>*</span>}
      </label>

      <Controller
        name={item.name}
        control={control}
        rules={{
          required: {
            value: isFieldRequired,
            message: `${item.label.en} is required`,
          },
          validate: () => {
            if (fileSizeError) {
              return fileSizeError;
            }
            return true;
          }
        }}
        render={({ fieldState, field: { value, onChange } }) => {
          if (item.multiple_file) {
            // Multiple file mode: manage file list ourselves to work around
            // PendingUpload's stale closure bug
            return (
              <>
                <span className='text-sm text-gray-500 my-2'>Max File size {item.max_file_size || 1} MB</span>
                <PendingUpload
                  asFile
                  onDownload={onDownload}
                  onChange={(file) => handleMultipleFileChange(file, onChange)}
                  allowedExtension={item.extensions ? item.extensions.split(',') : []}
                  disabled={item.disabled}
                  required={isFieldRequired}
                  multiple={true}
                  hidePreview
                />
                {/* Display accumulated files ourselves */}
                {accumulatedFiles.length > 0 && (
                  <div className="flex flex-col gap-2 mt-2">
                    {accumulatedFiles.map((f, idx) => {
                      const fileName = f instanceof File ? f.name : (f?.name || f?.valueInfo?.filename || `File ${idx + 1}`);
                      const fileSize = f instanceof File ? f.size : f?.size;
                      return (
                        <div key={`${fileName}-${idx}`} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm truncate">{fileName}</span>
                            {fileSize && (
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                ({(Number(fileSize) / 1024).toFixed(1)} KB)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {f?.original_url && (
                              <button
                                type="button"
                                className="text-blue-500 hover:text-blue-700 p-1"
                                onClick={() => onDownload(f)}
                              >
                                <FaDownload size={14} />
                              </button>
                            )}
                            {!item.disabled && (
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700 p-1"
                                onClick={() => handleRemoveFile(idx, onChange)}
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {fileSizeError && <p className="text-sm text-red-500">{fileSizeError}</p>}
                {fieldState.error && !fileSizeError && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
              </>
            );
          }

          // Single file mode: use PendingUpload normally
          return (
            <>
              <span className='text-sm text-gray-500 my-2'>Max File size {item.max_file_size || 1} MB</span>
              <PendingUpload
                asFile
                onDownload={onDownload}
                defaultValue={defaultValue}
                onChange={file => {
                  if (!file) return;
                  const maxSize = (item.max_file_size) * 1024 * 1024;
                  if (Array.isArray(file)) {
                    const tooLarge = file.find(f => f.size > maxSize);
                    if (tooLarge) {
                      setFileSizeError(`File size must be less than ${item.max_file_size} MB`);
                      return;
                    }
                    setFileSizeError(null);
                    onChange(file);
                    return;
                  }
                  if (file.size > maxSize) {
                    setFileSizeError(`File size must be less than ${item.max_file_size} MB`);
                    return;
                  }
                  setFileSizeError(null);
                  onChange(file);
                }}
                allowedExtension={item.extensions ? item.extensions.split(',') : []}
                disabled={item.disabled}
                required={isFieldRequired}
                multiple={false}
              />
              {fileSizeError && <p className="text-sm text-red-500">{fileSizeError}</p>}
              {fieldState.error && !fileSizeError && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
            </>
          );
        }}
      ></Controller>
    </div>
  );
}
